import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

const ImageModal = ({ isOpen, imageSrc, imageAlt, onClose }) => {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  const handleZoom = (factor) => {
    setZoom((prev) => {
      const newZoom = prev * factor;
      return Math.max(0.5, Math.min(3, newZoom));
    });
  };

  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative max-w-7xl max-h-full p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 text-white hover:text-gray-300 transition-colors duration-200 bg-black/50 rounded-full p-2 hover:bg-black/70"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Image Container */}
        <div className="flex flex-col items-center space-y-4">
          <div
            className="relative overflow-hidden rounded-lg shadow-2xl cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              src={imageSrc}
              alt={imageAlt}
              className="max-w-full max-h-[80vh] object-contain transition-transform duration-200"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${
                  position.y / zoom
                }px)`,
                cursor: zoom > 1 ? "grab" : "default",
              }}
              draggable={false}
            />
          </div>

          {/* Controls - Now positioned under the image */}
          <div className="flex space-x-2 mt-4">
            <button
              onClick={() => handleZoom(0.8)}
              disabled={zoom <= 0.5}
              className="bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <ZoomOut className="w-4 h-4" />
              <span className="text-sm">
                {t("blogPost.imageModal.zoomOut")}
              </span>
            </button>

            <button
              onClick={() => handleZoom(1.2)}
              disabled={zoom >= 3}
              className="bg-white/20 hover:bg-white/30 disabled:bg-white/10 disabled:cursor-not-allowed text-white px-3 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <ZoomIn className="w-4 h-4" />
              <span className="text-sm">{t("blogPost.imageModal.zoomIn")}</span>
            </button>

            <button
              onClick={resetView}
              className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm">{t("blogPost.imageModal.reset")}</span>
            </button>
          </div>
        </div>

        {/* Zoom Level Indicator */}
        <div className="absolute top-6 left-6 text-white text-sm bg-black/50 px-3 py-1 rounded-lg">
          {Math.round(zoom * 100)}%
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
